// [BB-EXP] - AT1 auto: Composant de feedback pour messages AT1 automatisés
// Affiche deux icônes pouces (👍👎) pour recueillir le feedback agent
// - Pouce levé : envoie directement un feedback positif
// - Pouce baissé : ouvre une modal pour commentaire puis envoie feedback négatif

import { useState } from 'react';
import { Box, Button, Typography } from '@evaneos/b2b-ui';
import ThumbUpOutlined from '@evaneos/b2b-ui/icons/ThumbUpOutlined';
import ThumbDownOutlined from '@evaneos/b2b-ui/icons/ThumbDownOutlined';
import ThumbUp from '@evaneos/b2b-ui/icons/ThumbUp';
import ThumbDown from '@evaneos/b2b-ui/icons/ThumbDown';

import { useIntl } from 'react-intl';

import { useTrackEventAmplitude, TripProperties } from '../../../../../shared/analytics';
import { sendFeedbackScore } from '../../../../../shared/services/langfuse';
import { getAgentDetailsFromEtsToken } from '../../../../../resources/login/operations';

import { AT1FeedbackNegativeModal } from './AT1FeedbackNegativeModal';

const AT1_AUTO_FEEDBACK_KEY = 'at1_auto_feedbacks';

const getAt1AutoFeedback = (dossierId: string): 'up' | 'down' | 'unknown' | null => {
    try {
        const stored = localStorage.getItem(AT1_AUTO_FEEDBACK_KEY);
        if (!stored) return null;
        const value = JSON.parse(stored)[dossierId];
        if (value === 'up' || value === 'down') return value;
        if (value) return 'unknown';
        return null;
    } catch {
        return null;
    }
};

const saveAt1AutoFeedback = (dossierId: string, vote: 'up' | 'down'): void => {
    try {
        const stored = localStorage.getItem(AT1_AUTO_FEEDBACK_KEY);
        const data = stored ? JSON.parse(stored) : {};
        data[dossierId] = vote;
        localStorage.setItem(AT1_AUTO_FEEDBACK_KEY, JSON.stringify(data));
    } catch {
        // silent fail
    }
};

interface Props {
    dossierId: string;
    trackedTripProperties: TripProperties;
    traceId?: string;
}

export const AT1FeedbackThumbs = ({ dossierId, trackedTripProperties, traceId }: Props) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const [negativeModalOpen, setNegativeModalOpen] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState<'up' | 'down' | 'unknown' | null>(() =>
        getAt1AutoFeedback(dossierId),
    );

    const handleThumbsUp = () => {
        setSelectedFeedback('up');
        saveAt1AutoFeedback(dossierId, 'up');
        trackEvent((ampli, defaultProperties) => {
            ampli.giveFeedbackClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                answer: 'Yes',
                ets_source: 'request_manager',
                is_at1_auto: true,
            });
        });
        if (traceId) {
            const agentDetails = getAgentDetailsFromEtsToken();
            void sendFeedbackScore(traceId, 1, undefined, {
                dossier_id: dossierId,
                agent_id: agentDetails?.agentId,
                agency_id: agentDetails?.agencyId,
            });
        }
    };

    const handleThumbsDown = () => {
        setNegativeModalOpen(true);
    };

    const handleNegativeModalClose = (comment: string) => {
        setNegativeModalOpen(false);
        if (comment) {
            setSelectedFeedback('down');
            saveAt1AutoFeedback(dossierId, 'down');
            trackEvent((ampli, defaultProperties) => {
                ampli.giveFeedbackClicked({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    answer: 'No',
                    content_feedback: comment,
                    ets_source: 'request_manager',
                    is_at1_auto: true,
                });
            });
            if (traceId) {
                const agentDetails = getAgentDetailsFromEtsToken();
                void sendFeedbackScore(traceId, 0, comment, {
                    dossier_id: dossierId,
                    agent_id: agentDetails?.agentId,
                    agency_id: agentDetails?.agencyId,
                });
            }
        }
    };

    if (selectedFeedback !== null) {
        return selectedFeedback === 'up' ? (
            <ThumbUp
                aria-hidden={undefined}
                role="img"
                aria-label={formatMessage({ id: 'discussion.at1_feedback.thumbs_up' })}
                sx={{ fontSize: '16px', color: 'text.secondary', marginRight: 1 }}
            />
        ) : selectedFeedback === 'down' ? (
            <ThumbDown
                aria-hidden={undefined}
                role="img"
                aria-label={formatMessage({ id: 'discussion.at1_feedback.thumbs_down' })}
                sx={{ fontSize: '16px', color: 'text.secondary', marginRight: 1 }}
            />
        ) : null;
    }

    return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', marginRight: 1 }}>
            <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={handleThumbsUp}
                startIcon={
                    <ThumbUpOutlined
                        sx={{ fontSize: '16px !important', color: 'text.secondary' }}
                    />
                }
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    component="span"
                >
                    {formatMessage({ id: 'discussion.at1_feedback.thumbs_up' })}
                </Typography>
            </Button>
            <Button
                variant="text"
                size="small"
                onClick={handleThumbsDown}
                startIcon={
                    <ThumbDownOutlined
                        sx={{ fontSize: '16px !important', color: 'text.secondary' }}
                    />
                }
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    component="span"
                >
                    {formatMessage({ id: 'discussion.at1_feedback.thumbs_down' })}
                </Typography>
            </Button>

            <AT1FeedbackNegativeModal open={negativeModalOpen} onClose={handleNegativeModalClose} />
        </Box>
    );
};
